import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  WBAlert,
  WBButton,
  WBCard,
  WBFlex,
  WBLinkButton,
  WBIcon,
  WBTypography,
} from "@apptractive/design-system-web";
import { stripeGetAmountCurrency } from "@apptractive/stm-shared";
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Switch,
} from "@mui/material";
import { Auth, Cache } from "aws-amplify";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  OnboardingStatus,
  StripePrice,
  StripePriceInterval,
  StripeProduct,
  SubscriptionStatus,
} from "../API";
import { isLoggedInVar } from "../customGraphql/cache";
import { CSGetSub as GET_SUB } from "../customGraphql/queries";
import {
  stripePaymentCheckout as CREATE_CHECKOUT,
  stripeUpdateSubscription as STRIPE_UPDATE_SUBSCRIPTION,
} from "../graphql/mutations";
import {
  getUser as GET_USER,
  stripeListPrices as STRIPE_LIST_PRICES,
  stripeListProducts as STRIPE_LIST_PRODUCTS,
} from "../graphql/queries";
import { getOnboardingPath } from "../helpers/onboarding";

interface StripeProductPrice extends StripeProduct {
  [StripePriceInterval.day]?: StripePrice;
  [StripePriceInterval.week]?: StripePrice;
  [StripePriceInterval.month]?: StripePrice;
  [StripePriceInterval.year]?: StripePrice;
}

interface StripePlansProps {
  path: string;
  isOnboarding: boolean;
}

const countryCurrencies: any = {
  AU: "aud",
  CH: "chf",
  US: "usd",
};

export const StripePlans = ({ path, isOnboarding }: StripePlansProps) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const navigate = useNavigate();
  const { data: productsData, loading: productsLoading } = useQuery(
    gql(STRIPE_LIST_PRODUCTS)
  );
  const { data: pricesData, loading: pricesLoading } = useQuery(
    gql(STRIPE_LIST_PRICES)
  );
  const { data: subData } = useQuery(gql(GET_SUB));
  const [getUser, { data: userData }] = useLazyQuery(gql(GET_USER));
  const [
    createCheckout,
    { data: checkoutData, error: checkoutError, loading: checkoutLoading },
  ] = useMutation(gql(CREATE_CHECKOUT));

  const [
    stripeUpdateSubscription,
    { error: updateError, loading: updateLoading },
  ] = useMutation(gql(STRIPE_UPDATE_SUBSCRIPTION));

  const [subscriptionPeriod, setSubscriptionPeriod] =
    useState<StripePriceInterval>(StripePriceInterval.month);

  useEffect(() => {
    const retrieveUser = async () => {
      try {
        await getUser({
          variables: {
            id: subData?.sub,
          },
          fetchPolicy: "network-only",
        });
      } catch (err) {
        console.log("ERROR get user : ", err);
      }
    };

    if (subData?.sub) {
      retrieveUser();
    }
  }, [getUser, subData]);

  useEffect(() => {
    const refreshUserSession = async () => {
      await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
    };

    refreshUserSession();
  }, []);

  useEffect(() => {
    if (checkoutData?.stripePaymentCheckout?.url) {
      window.location.replace(checkoutData?.stripePaymentCheckout?.url);
    }
  }, [checkoutData]);

  const user = useMemo(() => userData?.getUser || {}, [userData]);
  const billing = useMemo(() => userData?.getUser?.billing || {}, [userData]);

  useEffect(() => {
    if (isOnboarding && user?.onboardingStatus === OnboardingStatus.COMPLETED) {
      navigate(getOnboardingPath(user), { replace: true });
    }
  }, [user, isOnboarding, navigate]);

  const productsPrices: StripeProductPrice[] = useMemo(() => {
    if (!isEmpty(productsData) && !isEmpty(pricesData) && !isEmpty(user)) {
      const userProducts = productsData?.stripeListProducts.filter(
        (product: StripeProduct) => product.metadata?.userType === user.userType
      );
      const sortedUserProducts = userProducts.sort(
        (a: StripeProduct, b: StripeProduct) =>
          //@ts-ignore
          parseInt(a.metadata.order) - parseInt(b.metadata.order)
      );

      return sortedUserProducts.map((product: StripeProduct) => {
        const monthPrice = pricesData?.stripeListPrices.find(
          (price: StripePrice) =>
            price.product === product.id &&
            price.recurring?.interval === StripePriceInterval.month
        );

        const yearPrice = pricesData?.stripeListPrices.find(
          (price: StripePrice) =>
            price.product === product.id &&
            price.recurring?.interval === StripePriceInterval.year
        );

        const monthCurrencyOptions = JSON.parse(monthPrice.currency_options);
        const yearCurrencyOptions = JSON.parse(yearPrice.currency_options);

        return {
          month: {
            ...monthPrice,
            currency: countryCurrencies[user?.country],
            unit_amount:
              monthCurrencyOptions[countryCurrencies[user?.country]]
                .unit_amount,
          },
          year: {
            ...yearPrice,
            currency: countryCurrencies[user?.country],
            unit_amount:
              yearCurrencyOptions[countryCurrencies[user?.country]].unit_amount,
          },
          ...product,
        };
      });
    } else {
      return [];
    }
  }, [productsData, pricesData, user]);

  const onCreateCheckout = async (priceId: string, currency: string) => {
    if (!checkoutLoading) {
      try {
        await createCheckout({
          variables: {
            input: {
              priceId,
              returnUrl: window.location.href,
              successUrl: `${window.location.protocol}//${
                window.location.hostname
              }${
                window.location.port
                  ? `:${window.location.port}/${path}`
                  : `/${path}`
              }`,
              locale: user?.locale || "en",
              currency,
            },
          },
        });
      } catch (err) {
        console.log("errerrerr: ", err);
      }
    }
  };

  const onLogOut = async () => {
    client.cache.evict({ fieldName: "me" });
    client.cache.gc();

    try {
      localStorage.removeItem("sub");
      await Cache.clear();
      await Auth.signOut();
      isLoggedInVar(false);
    } catch (err) {
      console.log("ERROR log out: ", err);
    }
  };

  const getButtonTitle = (productId?: string) => {
    if (
      billing.planId === productId &&
      billing?.status !== SubscriptionStatus.CANCELLED &&
      billing?.status !== SubscriptionStatus.INCOMPLETE_EXPIRED
    ) {
      return t("selectedTitle", { ns: "common" });
    }

    if (!billing?.trialUsed) {
      return t("trialOneMonth", { ns: "purchase" });
    }

    return t("selectTitle", { ns: "common" });

    //{!billing?.trialUsed
    //  ? t("trialOneMonth", { ns: "purchase" })
    //  : t("selectTitle", { ns: "common" })}
  };

  const onCheckoutClick = async (product: StripeProductPrice) => {
    // subscription upgrade / downgrade workflow for existing active subscription
    if (
      billing?.subscriptionId &&
      billing?.status !== SubscriptionStatus.CANCELLED &&
      billing?.status !== SubscriptionStatus.INCOMPLETE_EXPIRED
    ) {
      const confirm = window.confirm(
        t("confirmUpgradeDowngradeSubscription", { ns: "purchase" })
      );

      if (confirm) {
        // upgrade / downgrade Stripe subscription
        try {
          await stripeUpdateSubscription({
            variables: {
              input: {
                priceId: product[subscriptionPeriod]!.id,
              },
            },
          });
        } catch (err) {
          console.log("ERROR stripeUpdateSubscription: ", err);
        }

        await window.alert(t("subscriptionUpdated", { ns: "purchase" }));

        try {
          // refresh user & billing
          await getUser({
            variables: {
              id: subData?.sub,
            },
            fetchPolicy: "network-only",
          });
        } catch (err) {
          console.log("ERROR get user: ", err);
        }
      }
    }

    // create stripe checkout session for new subscription
    else {
      try {
        await onCreateCheckout(
          product[subscriptionPeriod]!.id,
          product[subscriptionPeriod]!.currency
        );
      } catch (err) {
        console.log("ERROR create stripe checkout: ", err);
      }
    }
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{ my: 3 }}
      >
        <WBTypography fontWeight={600}>
          {t("billedMonthly", { ns: "purchase" })}
        </WBTypography>
        <Switch
          checked={subscriptionPeriod === StripePriceInterval.year}
          onChange={(e, checked) =>
            setSubscriptionPeriod(
              checked ? StripePriceInterval.year : StripePriceInterval.month
            )
          }
        />
        <WBTypography fontWeight={600}>
          {t("billedYearly", { ns: "purchase" })}{" "}
          <Box component="span" color="success.main">
            ({t("saveTitle", { ns: "common" })} 20%)
          </Box>
        </WBTypography>
      </Stack>
      <Grid
        container
        spacing={2}
        justifyContent={productsPrices.length === 1 ? "center" : undefined}
        mt={2}
      >
        {(productsLoading || pricesLoading) &&
          [...Array(3)].map((n, index) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              lg={4}
              key={`price-loading-${index}`}
            >
              <Skeleton variant="rectangular" width="100%" height="200px" />
            </Grid>
          ))}
        {productsPrices &&
          productsPrices?.map((product: StripeProductPrice) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={productsPrices.length > 1 ? 12 / productsPrices.length : 6}
              lg={productsPrices.length > 1 ? 12 / productsPrices.length : 6}
              key={product.id}
            >
              <WBCard sx={{ p: 3 }}>
                <WBFlex flexDirection="column">
                  <WBTypography variant="h3" fontWeight="bold">
                    {product.name}
                  </WBTypography>
                  {product &&
                    product?.[subscriptionPeriod] &&
                    product?.[subscriptionPeriod]?.unit_amount != null &&
                    typeof product?.[subscriptionPeriod]?.unit_amount ===
                      "number" && (
                      <WBTypography fontSize="2rem" fontWeight="bold">
                        {stripeGetAmountCurrency(
                          product[subscriptionPeriod]!.currency,
                          product[subscriptionPeriod]!.unit_amount,
                          false
                        )}
                        <Box component="span" fontSize="0.7rem">
                          {" "}
                          / {t(subscriptionPeriod, { ns: "common" })}
                        </Box>
                      </WBTypography>
                    )}
                  {/*<WBTypography sx={{ mt: 1 }}>*/}
                  {/*  {product.description}*/}
                  {/*</WBTypography>*/}
                  <List dense>
                    <ListItem sx={{ mt: 2 }}>
                      <ListItemIcon>
                        <WBIcon
                          library="ioniconSharp"
                          name="CheckmarkCircle"
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${
                          product?.[subscriptionPeriod]?.metadata
                            ?.monthlyAccessQuantity === "unlimited"
                            ? t("unlimitedTitle", { ns: "common" })
                            : product?.[subscriptionPeriod]?.metadata
                                ?.monthlyAccessQuantity
                        } ${t("productsPerMonth", { ns: "purchase" })}`}
                      />
                    </ListItem>
                    {product?.[subscriptionPeriod]?.metadata?.userSeats && (
                      <ListItem>
                        <ListItemIcon>
                          <WBIcon
                            library="ioniconSharp"
                            name="CheckmarkCircle"
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${
                            product?.[subscriptionPeriod]?.metadata?.userSeats
                          } ${t("teamUsers", { ns: "purchase" })}`}
                        />
                      </ListItem>
                    )}
                    {!billing?.trialUsed && (
                      <ListItem>
                        <ListItemIcon>
                          <WBIcon
                            library="ioniconSharp"
                            name="CheckmarkCircle"
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={t("thirtyDaysTrial", { ns: "purchase" })}
                        />
                      </ListItem>
                    )}
                  </List>
                  <WBButton
                    size="small"
                    sx={{ mt: 2 }}
                    disabled={
                      (billing?.productId === product[subscriptionPeriod]!.id ||
                        billing?.planId === product[subscriptionPeriod]!.id ||
                        checkoutLoading ||
                        updateLoading) &&
                      billing?.status !== SubscriptionStatus.CANCELLED &&
                      billing?.status !== SubscriptionStatus.INCOMPLETE_EXPIRED
                    }
                    onClick={() => onCheckoutClick(product)}
                  >
                    {getButtonTitle(product[subscriptionPeriod]!.id)}
                    {/*{!billing?.trialUsed*/}
                    {/*  ? t("trialOneMonth", { ns: "purchase" })*/}
                    {/*  : t("selectTitle", { ns: "common" })}*/}
                  </WBButton>
                </WBFlex>
              </WBCard>
            </Grid>
          ))}
      </Grid>

      {(!productsLoading || !pricesLoading) && (
        <WBLinkButton sx={{ mt: 5 }} onClick={onLogOut}>
          {t("logOut", { ns: "common" })}
        </WBLinkButton>
      )}

      {(!isEmpty(checkoutError) || !isEmpty(updateError)) && (
        <WBAlert
          title={checkoutError?.message || updateError?.message}
          severity="error"
          sx={{ my: 2 }}
        />
      )}
    </>
  );
};
